import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Book, Map, Brain, MessageSquare, User, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from './AuthModal';

function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsMobileMenuOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 font-serif ${
      isScrolled ? 'bg-parchment shadow-vintage' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <Book className="h-8 w-8 text-parchment-dark" />
              <div className="hidden sm:flex flex-col">
                <span className="text-lg font-bold text-parchment-dark">History Tutor</span>
                <span className="text-xs text-parchment-dark/60">AI-Powered Learning</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/landingpage" className="text-parchment-dark hover:text-parchment-dark/80 px-3 py-2 transition-colors">Home</Link>
            <Link to="/timeline" className="text-parchment-dark hover:text-parchment-dark/80 px-3 py-2 transition-colors">Timeline</Link>
            <Link to="/familytree" className="text-parchment-dark hover:text-parchment-dark/80 px-3 py-2 transition-colors">Family Tree</Link>
            <Link to="/map" className="text-parchment-dark hover:text-parchment-dark/80 px-3 py-2 transition-colors">World History</Link>
            {user && (
              <>
                <Link to="/quiz" className="text-parchment-dark hover:text-parchment-dark/80 px-3 py-2 transition-colors">Quiz</Link>
                <Link to="/chat" className="text-parchment-dark hover:text-parchment-dark/80 px-3 py-2 transition-colors">AI Tutor</Link>
              </>
            )}
            <Link to="/about" className="text-parchment-dark hover:text-parchment-dark/80 px-3 py-2 transition-colors">About</Link>
            {user ? (
  <div className="flex items-center space-x-2">
    <div className="flex items-center text-parchment-dark">
      <User className="h-5 w-5 mr-1" />
      <Link
        to="/dashboard"
        className="hover:underline hover:text-parchment-dark/80 transition-colors"
      >
        {user.email?.split('@')[0]}
      </Link>
    </div>
    <button
      onClick={handleSignOut}
      className="text-parchment-dark hover:text-parchment-dark/80 transition-colors"
    >
      <LogOut className="h-5 w-5" />
    </button>
  </div>
            ) : (
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="text-parchment-dark hover:text-parchment-dark/80 px-3 py-2 transition-colors"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-parchment-dark hover:text-parchment-dark/80 transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-parchment shadow-vintage">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link 
              to="/timeline" 
              className="flex items-center gap-2 px-3 py-2 text-parchment-dark hover:bg-parchment-light rounded-md transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Book className="h-5 w-5" />
              Timeline
            </Link>
            <Link 
              to="/map" 
              className="flex items-center gap-2 px-3 py-2 text-parchment-dark hover:bg-parchment-light rounded-md transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Map className="h-5 w-5" />
              World History
            </Link>
            {user && (
              <>
                <Link 
                  to="/quiz" 
                  className="flex items-center gap-2 px-3 py-2 text-parchment-dark hover:bg-parchment-light rounded-md transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Brain className="h-5 w-5" />
                  Quiz
                </Link>
                <Link 
                  to="/chat" 
                  className="flex items-center gap-2 px-3 py-2 text-parchment-dark hover:bg-parchment-light rounded-md transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <MessageSquare className="h-5 w-5" />
                  AI Tutor
                </Link>
              </>
            )}
            {user ? (
              <>
                <div className="flex items-center gap-2 px-3 py-2 text-parchment-dark">
                  <User className="h-5 w-5" />
                  <span>{user.email?.split('@')[0]}</span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-3 py-2 text-parchment-dark hover:bg-parchment-light rounded-md transition-colors w-full"
                >
                  <LogOut className="h-5 w-5" />
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setIsAuthModalOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 px-3 py-2 text-parchment-dark hover:bg-parchment-light rounded-md transition-colors w-full"
              >
                <User className="h-5 w-5" />
                Sign In
              </button>
            )}
          </div>
        </div>
      )}

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />
    </nav>
  );
}

export default Navbar;